import type { ComponentType } from 'react';

import type { AsyncJobSummary } from '@core/types/api/AsyncJobSummary/AsyncJobSummary';
import Enums from '@core/types/api/Enums';
import type { QueryRouteBarrelKeys } from '@web/utils/ApiClient';

import {
  QuotePriceEscalationFailedModal,
  QuotePriceEscalationFailedPreview,
} from './QuotePriceEscalationFailedModal';
import {
  QuotePricingUpdateFailedModal,
  QuotePricingUpdateFailedPreview,
} from './QuotePricingUpdateFailedModal';

export type AsyncJobFailureDetailsProps = {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  summaryFailedCount: number;
  isProcessing: boolean;
};

export type AsyncJobFailurePreviewProps = {
  jobId: string;
  summaryFailedCount: number;
  isProcessing: boolean;
  actionLabel: string;
  onViewFailures: () => void;
};

export type AsyncJobStatusConfig = {
  jobType: Enums.AsyncJobType;
  title: string;
  processingLabel: (job: AsyncJobSummary, displayProcessedCount?: number) => string;
  compactProcessingLabel: (job: AsyncJobSummary, displayProcessedCount?: number) => string;
  successLabel: (job: AsyncJobSummary) => string;
  failedTitle: string;
  failedMessage: string;
  failureDetails?: {
    actionLabel: string;
    Component: ComponentType<AsyncJobFailureDetailsProps>;
    PreviewComponent: ComponentType<AsyncJobFailurePreviewProps>;
  };
  terminalInvalidationRouteKeys?: QueryRouteBarrelKeys[];
};

const quotePricingUpdateConfig: AsyncJobStatusConfig = {
  jobType: Enums.AsyncJobType.QuotePricingUpdate,
  title: 'Bulk Price Update',
  processingLabel: (job, displayProcessedCount = job.itemsProcessed) =>
    `Updating quotes... ${displayProcessedCount} of ${job.itemsTotal}`,
  compactProcessingLabel: (job, displayProcessedCount = job.itemsProcessed) =>
    `Updating ${displayProcessedCount}/${job.itemsTotal}...`,
  successLabel: (job) =>
    job.itemsTotal === 1
      ? 'Quote updated successfully'
      : `All ${job.itemsTotal} quotes updated successfully`,
  failedTitle: 'Update Failed',
  failedMessage: 'The bulk price update job failed. Please try again.',
  failureDetails: {
    actionLabel: 'View Failed Quotes',
    Component: QuotePricingUpdateFailedModal,
    PreviewComponent: QuotePricingUpdateFailedPreview,
  },
  terminalInvalidationRouteKeys: [
    'GET quotes',
    'GET quotes typeahead',
    'GET quote summaries',
    'GET quote status aggregations',
    'GET quote by ID',
    'GET quote products by quote ID',
    'GET quote revisions by quote ID',
    'GET quote suspended orders by quote ID',
    'GET project quotes by project ID',
    'GET quote bulk pricing update preview',
  ],
};

const quotePriceEscalationsConfig: AsyncJobStatusConfig = {
  jobType: Enums.AsyncJobType.QuotePriceEscalation,
  title: 'Quote Price Escalations',
  processingLabel: (job, displayProcessedCount = job.itemsProcessed) =>
    `Applying Escalations... ${displayProcessedCount} of ${job.itemsTotal}`,
  compactProcessingLabel: (job, displayProcessedCount = job.itemsProcessed) =>
    `Applying Escalations... ${displayProcessedCount}/${job.itemsTotal}`,
  successLabel: (job) =>
    job.itemsTotal === 1
      ? 'Escalation applied successfully'
      : `All ${job.itemsTotal} escalations applied successfully`,
  failedTitle: 'Escalations Failed',
  failedMessage: 'The quote price escalations job failed. Please try again.',
  failureDetails: {
    actionLabel: 'View Failed Escalations',
    Component: QuotePriceEscalationFailedModal,
    PreviewComponent: QuotePriceEscalationFailedPreview,
  },
  terminalInvalidationRouteKeys: [
    'GET quote price escalations',
    'GET quote escalations stats',
    'GET quote price escalation summaries',
    'GET quotes',
    'GET quotes typeahead',
    'GET quote summaries',
    'GET quote status aggregations',
    'GET quote by ID',
    'GET quote products by quote ID',
    'GET quote revisions by quote ID',
    'GET quote suspended orders by quote ID',
    'GET project quotes by project ID',
  ],
};

export const ASYNC_JOB_STATUS_CONFIGS: Partial<Record<Enums.AsyncJobType, AsyncJobStatusConfig>> = {
  [Enums.AsyncJobType.QuotePricingUpdate]: quotePricingUpdateConfig,
  [Enums.AsyncJobType.QuotePriceEscalation]: quotePriceEscalationsConfig,
};

export const ASYNC_JOB_STATUS_JOB_TYPES = Object.keys(
  ASYNC_JOB_STATUS_CONFIGS,
) as Enums.AsyncJobType[];

export const asyncJobStatusConfigFor = (
  jobType: Enums.AsyncJobType,
): AsyncJobStatusConfig | undefined => ASYNC_JOB_STATUS_CONFIGS[jobType];
